import React, { useState, useMemo, Fragment } from "react";
import {
  HiSearch,
  HiX,
  HiUser,
  HiShoppingBag,
  HiGift,
  HiStar,
  HiUserAdd,
  HiOutlinePlusSm,
  HiOutlineBadgeCheck,
  HiOutlineUserGroup,
  HiOutlineFilter,
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineSortAscending,
  HiOutlineTrash
} from "react-icons/hi";
import { HiTrophy } from "react-icons/hi2";
import "./MembersList.css";
import { useDispatch, useSelector } from "react-redux";
import { createMemberAction, deleteMemberAction } from "../../redux/actions/memberActions";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import Button from "../shared/Button";
import TextInput from "../shared/TextInput";
import Card from "../shared/Card";
import Badge from "../shared/Badge";
import Modal from "../shared/Modal";
import { buildMemberGroupMap, formatMemberWithGroups } from "../../utils/memberGroups";

export const MembersList = ({ members, onMemberClick }) => {
  const dispatch = useDispatch();
  const currentOrganisation = useSelector((state) => state.organisation?.currentOrganisation);
  const groups = useSelector((state) => state.organisation?.currentOrganisation?.groups);
  const memberGroupMap = useMemo(() => buildMemberGroupMap(groups), [groups]);
  const isAdmin = useIsAdmin();
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    minPoints: "",
    maxPoints: "",
    hasTransactions: false
  });
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [newMember, setNewMember] = useState({ name: "", identification: "" });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSearch = (event) => setSearchTerm(event.target.value);
  const clearSearch = () => setSearchTerm("");

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({ minPoints: "", maxPoints: "", hasTransactions: false });
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "grid" ? "list" : "grid");
  };

  const getAvatarLetter = (member) =>
    member.name
      ? member.name[0].toUpperCase()
      : member.identification
      ? member.identification[0].toUpperCase()
      : "?";

  const getUnrealizedValuePoints = (member) =>
    member.unrealizedValue?.reduce((sum, uv) => sum + (uv.value || 0), 0) || 0;

  const getRedeemedPoints = (member) =>
    member.redeemedPoints?.reduce((sum, uv) => sum + (uv.points || 0), 0) || 0;

  const filteredMembers =
    members?.filter((member) => {
      const matchesSearch =
        member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.identification?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesMinPoints =
        filters.minPoints === "" || (member.points || 0) >= Number(filters.minPoints);
      const matchesMaxPoints =
        filters.maxPoints === "" || (member.points || 0) <= Number(filters.maxPoints);
      const matchesTransactions =
        !filters.hasTransactions || (member.transactions && member.transactions.length > 0);
      return matchesSearch && matchesMinPoints && matchesMaxPoints && matchesTransactions;
    }) || [];

  const sortedMembers = [...filteredMembers].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case "name":
        comparison = (a.name || "").localeCompare(b.name || "");
        break;
      case "points":
        comparison = (a.points || 0) - (b.points || 0);
        break;
      case "redeemed":
        comparison = getRedeemedPoints(a) - getRedeemedPoints(b);
        break;
      case "transactions":
        comparison = (a.transactions?.length || 0) - (b.transactions?.length || 0);
        break;
      default:
        comparison = 0;
    }
    return sortOrder === "asc" ? comparison : -comparison;
  });

  const handleAddMember = async () => {
    if (!newMember.identification) {
      setError("Identification is required");
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      await dispatch(createMemberAction(currentOrganisation._id, newMember));
      setShowAddMemberModal(false);
      setNewMember({ name: "", identification: "" });
    } catch (err) {
      setError(err.message || "Failed to add member");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (member, event) => {
    event.stopPropagation();
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;
    setIsDeleting(true);
    setError("");
    try {
      await dispatch(deleteMemberAction(currentOrganisation._id, memberToDelete._id));
      setShowDeleteModal(false);
      setMemberToDelete(null);
    } catch (err) {
      setError(err.message || "Failed to delete member");
    } finally {
      setIsDeleting(false);
    }
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== "" && v !== false);

  return (
    <div className="p-4 md:p-8 min-h-screen bg-canvas members-list-container">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-surface-soft text-ink border border-hairline">
                <HiOutlineUserGroup className="h-6 w-6" />
              </div>
              <h1 className="text-display-md text-ink" data-help-title="Members" data-help="Members are the customers participating in your loyalty program. Browse everyone in your program, search by name or identification, and check the points they have earned.">Members</h1>
            </div>

            <div className="flex flex-wrap gap-2 self-end md:self-auto">
              <Button
                variant="secondary"
                size="sm"
                onClick={toggleViewMode}
                data-help-title="View toggle"
                data-help="Switches between a table layout with sortable columns and a card grid layout. The label and icon show the view you would switch to."
                leadingIcon={
                  viewMode === "grid" ? (
                    <HiOutlineViewList className="h-4 w-4" />
                  ) : (
                    <HiOutlineViewGrid className="h-4 w-4" />
                  )
                }
              >
                {viewMode === "grid" ? "List View" : "Grid View"}
              </Button>

              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                data-help-title="Filters"
                data-help="Shows or hides a filter panel with minimum points, maximum points, and has-transactions filters. A small coral dot on the button signals that filters are active."
                leadingIcon={<HiOutlineFilter className="h-4 w-4" />}
              >
                Filters
                {hasActiveFilters && (
                  <span className="ml-1 inline-block h-2 w-2 rounded-full bg-signature-coral" />
                )}
              </Button>

              {isAdmin && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowAddMemberModal(true)}
                  data-help-title="Add member"
                  data-help="Opens a form to manually register a member with an optional name and a required identification, the unique ID matched against incoming transactions. Only admins see this button."
                  leadingIcon={<HiUserAdd className="h-4 w-4" />}
                >
                  Add Member
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-grow">
              <TextInput
                type="text"
                placeholder="Search members by name or ID..."
                value={searchTerm}
                onChange={handleSearch}
                data-help-title="Search members"
                data-help="Filters the member list by name or identification as you type. The X button clears the search."
                leadingIcon={<HiSearch className="h-4 w-4" />}
                trailingIcon={
                  searchTerm ? (
                    <button onClick={clearSearch} aria-label="Clear search">
                      <HiX className="h-4 w-4" />
                    </button>
                  ) : null
                }
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant={sortBy === "name" ? "primary" : "secondary"}
                size="sm"
                onClick={() => toggleSort("name")}
                data-help-title="Sort by name"
                data-help="Sorts the member list alphabetically by name. Clicking again flips between ascending and descending; the arrow shows the current direction."
                leadingIcon={<HiOutlineSortAscending className="h-4 w-4" />}
              >
                Name {sortBy === "name" && (sortOrder === "asc" ? "↑" : "↓")}
              </Button>

              <Button
                variant={sortBy === "points" ? "primary" : "secondary"}
                size="sm"
                onClick={() => toggleSort("points")}
                data-help-title="Sort by points"
                data-help="Sorts the member list by current points balance. Clicking again flips between ascending and descending; the arrow shows the current direction."
                leadingIcon={<HiStar className="h-4 w-4" />}
              >
                Points {sortBy === "points" && (sortOrder === "asc" ? "↑" : "↓")}
              </Button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <Card variant="surface-soft" className="filter-panel p-6">
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1">
                  <TextInput
                    label="Min Points"
                    type="number"
                    value={filters.minPoints}
                    onChange={(e) => handleFilterChange("minPoints", e.target.value)}
                    placeholder="0"
                    min="0"
                    data-help-title="Min points filter"
                    data-help="Shows only members whose points balance is at least this number. Leave empty for no minimum."
                  />
                </div>
                <div className="flex-1">
                  <TextInput
                    label="Max Points"
                    type="number"
                    value={filters.maxPoints}
                    onChange={(e) => handleFilterChange("maxPoints", e.target.value)}
                    placeholder="Unlimited"
                    min="0"
                    data-help-title="Max points filter"
                    data-help="Shows only members whose points balance is at most this number. Leave empty for no limit."
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-body-md font-medium text-ink mb-1.5">
                    Has Transactions
                  </label>
                  <label className="inline-flex items-center cursor-pointer mt-2">
                    <input
                      type="checkbox"
                      checked={filters.hasTransactions}
                      onChange={(e) => handleFilterChange("hasTransactions", e.target.checked)}
                      className="rounded border-hairline text-ink focus:ring-info-border"
                      data-help-title="Has transactions filter"
                      data-help="When checked, shows only members who have at least one recorded transaction."
                    />
                    <span className="ml-2 text-body-md text-ink">
                      {filters.hasTransactions ? "Yes" : "No"}
                    </span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={resetFilters} data-help-title="Reset filters" data-help="Clears the minimum points, maximum points, and has-transactions filters so the full member list is shown again.">
                  Reset Filters
                </Button>
              </div>
            </Card>
          )}

          {/* Results count */}
          <div className="text-body-md text-muted" data-help-title="Results count" data-help="Shows how many members match the current search and filters, so you can tell when the view is narrowed.">
            Showing {sortedMembers.length} {sortedMembers.length === 1 ? "member" : "members"}
            {(searchTerm || hasActiveFilters) && " with applied filters"}
          </div>
        </div>

        {/* Grid View */}
        {viewMode === "grid" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sortedMembers.map((member, index) => (
              <Card
                key={member._id || index}
                variant="default"
                className="member-card cursor-pointer hover:shadow-card relative !p-0 overflow-hidden"
                onClick={() => onMemberClick(member)}
              >
                <div className="p-5">
                  <div className="flex justify-center">
                    <div className="h-16 w-16 rounded-full flex items-center justify-center text-ink text-title-sm font-medium border border-hairline bg-surface-soft">
                      {getAvatarLetter(member)}
                    </div>
                  </div>

                  <div className="text-center mt-3">
                    <h3 className="text-title-sm text-ink truncate">
                      {formatMemberWithGroups(member, memberGroupMap, "No Name")}
                    </h3>
                    <p className="text-body-md text-muted truncate">
                      {member.identification}
                    </p>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="p-3 rounded-md bg-surface-soft">
                      <div className="flex items-center gap-2 mb-1">
                        <HiStar className="h-4 w-4 text-ink" />
                        <span className="text-[12px] text-muted">Points</span>
                      </div>
                      <p className="font-medium text-ink">
                        {(member.points || 0).toLocaleString()}
                      </p>
                    </div>

                    <div className="p-3 rounded-md bg-surface-soft">
                      <div className="flex items-center gap-2 mb-1">
                        <HiShoppingBag className="h-4 w-4 text-ink" />
                        <span className="text-[12px] text-muted">Transactions</span>
                      </div>
                      <p className="font-medium text-ink">
                        {(member.transactions?.length || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div className="p-3 rounded-md bg-surface-soft">
                      <div className="flex items-center gap-2 mb-1">
                        <HiGift className="h-4 w-4 text-ink" />
                        <span className="text-[12px] text-muted">Redeemed</span>
                      </div>
                      <p className="font-medium text-ink">
                        {getRedeemedPoints(member).toLocaleString()}
                      </p>
                    </div>

                    <div className="p-3 rounded-md bg-surface-soft">
                      <div className="flex items-center gap-2 mb-1">
                        <HiTrophy className="h-4 w-4 text-ink" />
                        <span className="text-[12px] text-muted">Unrealized</span>
                      </div>
                      <p className="font-medium text-ink">
                        {getUnrealizedValuePoints(member).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {member.status && (
                    <div className="absolute top-3 right-3">
                      <Badge
                        variant={member.status === "active" ? "success" : "warning"}
                        size="sm"
                      >
                        {member.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  )}

                  {isAdmin && (
                    <div className="absolute top-3 left-3">
                      <button
                        onClick={(e) => handleDeleteClick(member, e)}
                        className="p-1.5 rounded-full bg-canvas border border-hairline text-muted hover:text-signature-coral transition-colors"
                        title="Delete Member"
                        data-help-title="Delete member"
                        data-help="Opens a confirmation dialog before deleting this member. Confirming permanently removes their points, transactions, and history. Only admins see this button."
                      >
                        <HiOutlineTrash className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* List View */}
        {viewMode === "list" && (
          <div className="overflow-hidden rounded-md border border-hairline bg-canvas">
            <div className="overflow-x-auto">
              <table className="w-full text-body-md" data-help-title="Members table" data-help="Lists every member with their transactions, points, redeemed, and unrealized values. Clicking the Member, Transactions, Points, or Redeemed headers re-sorts the table; clicking a row opens that member's full profile.">
                <thead>
                  <tr className="text-[12px] uppercase tracking-wide bg-surface-soft text-ink border-b border-hairline">
                    <th className="px-6 py-4 text-left font-medium">
                      <button
                        onClick={() => toggleSort("name")}
                        className="flex items-center"
                      >
                        Member
                        {sortBy === "name" && (
                          <span className="ml-1">{sortOrder === "asc" ? "↑" : "↓"}</span>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left font-medium hidden md:table-cell">
                      <button
                        onClick={() => toggleSort("transactions")}
                        className="flex items-center gap-2"
                      >
                        <HiShoppingBag className="h-4 w-4" />
                        <span>Transactions</span>
                        {sortBy === "transactions" && (
                          <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left font-medium">
                      <button
                        onClick={() => toggleSort("points")}
                        className="flex items-center gap-2"
                      >
                        <HiStar className="h-4 w-4" />
                        <span>Points</span>
                        {sortBy === "points" && (
                          <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left font-medium hidden lg:table-cell">
                      <button
                        onClick={() => toggleSort("redeemed")}
                        className="flex items-center gap-2"
                      >
                        <HiGift className="h-4 w-4" />
                        <span>Redeemed</span>
                        {sortBy === "redeemed" && (
                          <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left font-medium hidden lg:table-cell">
                      <div className="flex items-center gap-2">
                        <HiTrophy className="h-4 w-4" />
                        <span>Unrealized</span>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left font-medium">
                      <span>Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hairline">
                  {sortedMembers.map((member, index) => (
                    <tr
                      key={member._id || index}
                      onClick={() => onMemberClick(member)}
                      className="member-row cursor-pointer hover:bg-surface-soft"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 flex items-center justify-center rounded-full text-ink text-body-md font-medium bg-surface-soft border border-hairline">
                            {getAvatarLetter(member)}
                          </div>
                          <div>
                            <div className="font-medium text-ink flex items-center gap-2">
                              {formatMemberWithGroups(member, memberGroupMap, "No Name")}
                              {member.status && (
                                <Badge
                                  variant={member.status === "active" ? "success" : "warning"}
                                  size="sm"
                                >
                                  {member.status === "active" && (
                                    <HiOutlineBadgeCheck className="mr-1 h-3 w-3" />
                                  )}
                                  {member.status === "active" ? "Active" : "Inactive"}
                                </Badge>
                              )}
                            </div>
                            <div className="text-[12px] text-muted">
                              {member.identification}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell text-body">
                        {member.transactions ? member.transactions.length : 0}
                      </td>
                      <td className="px-6 py-4 font-medium text-ink">
                        {(member.points || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell text-body">
                        {getRedeemedPoints(member).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell text-body">
                        {getUnrealizedValuePoints(member).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        {isAdmin && (
                          <button
                            onClick={(e) => handleDeleteClick(member, e)}
                            className="p-2 rounded-md text-muted hover:text-signature-coral hover:bg-surface-soft transition-colors"
                            title="Delete Member"
                            data-help-title="Delete member"
                            data-help="Opens a confirmation dialog before deleting this member. Confirming permanently removes their points, transactions, and history. Only admins see this button."
                          >
                            <HiOutlineTrash className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {sortedMembers.length === 0 && (
                <div className="text-center py-16">
                  <div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center bg-surface-soft text-muted">
                    <HiUser className="h-6 w-6" />
                  </div>
                  <h3 className="mt-3 text-title-sm text-ink">No members found</h3>
                  <p className="mt-1 text-body-md text-muted">
                    Try adjusting your search or filters.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Floating Add Member Button (mobile) */}
        <div className="md:hidden fixed bottom-4 right-4 z-10">
          <button
            onClick={() => setShowAddMemberModal(true)}
            className="w-14 h-14 rounded-full flex items-center justify-center shadow-card bg-ink text-onInk"
          >
            <HiOutlinePlusSm className="h-8 w-8" />
          </button>
        </div>
      </div>

      {/* Add Member Modal */}
      <Modal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        title="Add New Member"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowAddMemberModal(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddMember}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Adding..." : "Add Member"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextInput
            label="Name (Optional)"
            type="text"
            value={newMember.name}
            onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
            placeholder="Enter member name"
          />
          <TextInput
            label="Identification *"
            type="text"
            value={newMember.identification}
            onChange={(e) =>
              setNewMember({ ...newMember, identification: e.target.value })
            }
            placeholder="Enter member identification"
            error={error}
          />
        </div>
      </Modal>

      {/* Delete Member Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setMemberToDelete(null);
          setError("");
        }}
        title="Delete Member"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowDeleteModal(false);
                setMemberToDelete(null);
                setError("");
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDeleteMember}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete Member"}
            </Button>
          </>
        }
      >
        <p className="text-body-md text-body">
          Are you sure you want to delete{" "}
          <span className="font-medium text-ink">
            {memberToDelete?.name || memberToDelete?.identification}
          </span>
          ? This action cannot be undone and will permanently remove all their
          data including points, transactions, and history.
        </p>
        {error && (
          <div className="mt-4 p-3 rounded-md border border-signature-coral bg-signature-coral/5">
            <p className="text-body-md text-signature-coral">{error}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MembersList;
